/**
 * Notification Alerts Hook
 *
 * Watches for new notifications and triggers:
 *  - Browser notification sound (Web Audio API chime)
 *  - Desktop notifications (Notification API)
 *
 * Respects user preferences (soundEnabled, desktopEnabled).
 * Skipped when the notification panel is open (user is already looking).
 *
 * AudioContext warmup: browsers block AudioContext.resume() unless it
 * originates from a user gesture. We install a one-time click/keydown
 * listener on mount to "warm up" the context so it's ready when a
 * WebSocket notification arrives later.
 */

import { useEffect, useRef, useCallback } from "react";
import {
  useNotificationStore,
  type AppNotification,
} from "@/stores/useNotificationStore";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { logger } from "@/services/logger";

// ============================================================
// Sound — Web Audio API with user-gesture warmup
// ============================================================

let audioCtx: AudioContext | null = null;
let audioWarmedUp = false;

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx || audioCtx.state === "closed") {
      audioCtx = new AudioContext();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

/**
 * Resume AudioContext from a user gesture so it's ready for later
 * programmatic playback (WebSocket events are not user gestures).
 */
function warmUpAudio(): void {
  if (audioWarmedUp) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === "suspended") {
    ctx
      .resume()
      .then(() => {
        audioWarmedUp = true;
      })
      .catch(() => {});
  } else {
    audioWarmedUp = true;
  }
}

// Install a one-time listener to warm up audio on first user interaction
if (typeof window !== "undefined") {
  const events = ["click", "keydown", "touchstart"] as const;
  const handler = () => {
    warmUpAudio();
    for (const evt of events) {
      document.removeEventListener(evt, handler);
    }
  };
  for (const evt of events) {
    document.addEventListener(evt, handler, { once: false, passive: true });
  }
}

/**
 * Play a short two-tone notification chime via Web Audio API.
 * No audio file required — synthesised on the fly.
 */
function playNotificationSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // If still suspended somehow, try resume (won't work without gesture but won't throw)
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
    return; // Skip this notification — sound will work from next one after a click
  }

  const now = ctx.currentTime;

  // Tone 1: C5 (523 Hz), 80ms
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = "sine";
  osc1.frequency.value = 523;
  gain1.gain.setValueAtTime(0.15, now);
  gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  osc1.connect(gain1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.08);

  // Tone 2: E5 (659 Hz), 120ms, slight delay
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = "sine";
  osc2.frequency.value = 659;
  gain2.gain.setValueAtTime(0.12, now + 0.06);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  osc2.connect(gain2).connect(ctx.destination);
  osc2.start(now + 0.06);
  osc2.stop(now + 0.18);
}

// ============================================================
// Desktop Notifications
// ============================================================

function showDesktopNotification(notification: AppNotification): void {
  if (!("Notification" in window)) return;
  if (window.Notification.permission !== "granted") return;

  // Don't show if the tab is focused — the in-app toast is sufficient
  if (document.hasFocus()) return;

  try {
    const n = new window.Notification(notification.title, {
      body: notification.message,
      icon: "/logo192.png",
      tag: notification.id, // Deduplication
      silent: true, // We play our own sound
    });

    // Auto-close after 5 seconds
    setTimeout(() => n.close(), 5000);

    // Focus tab on click
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch (err) {
    logger.warn("Failed to show desktop notification", {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Request desktop notification permission (non-blocking).
 * Should be called after a user gesture (e.g. toggling the preference on).
 */
export function requestDesktopPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window))
    return Promise.resolve("denied" as NotificationPermission);
  if (window.Notification.permission === "granted")
    return Promise.resolve("granted");
  if (window.Notification.permission === "denied")
    return Promise.resolve("denied");

  return window.Notification.requestPermission();
}

// ============================================================
// Hook
// ============================================================

export function useNotificationAlerts(): void {
  const pendingToast = useNotificationStore((s) => s.pendingToast);
  const isPanelOpen = useNotificationStore((s) => s.isPanelOpen);
  const { data: preferences } = useNotificationPreferences();

  // Track the last processed notification to avoid re-firing
  const lastProcessedId = useRef<string | null>(null);

  const fireAlerts = useCallback(
    (notification: AppNotification) => {
      if (!preferences?.globalEnabled) return;

      if (preferences.soundEnabled) {
        playNotificationSound();
      }

      if (preferences.desktopEnabled) {
        showDesktopNotification(notification);
      }
    },
    [
      preferences?.globalEnabled,
      preferences?.soundEnabled,
      preferences?.desktopEnabled,
    ],
  );

  useEffect(() => {
    if (!pendingToast) return;
    if (pendingToast.id === lastProcessedId.current) return;

    // Skip alerts if the panel is already open (user is looking)
    if (isPanelOpen) {
      lastProcessedId.current = pendingToast.id;
      return;
    }

    lastProcessedId.current = pendingToast.id;
    fireAlerts(pendingToast);
  }, [pendingToast, isPanelOpen, fireAlerts]);
}
